import React from "react"
import { graphql } from "gatsby"
import { useIntl } from "gatsby-plugin-react-intl"
import styled from "styled-components"

import Layout from "../layouts/Layout"
import { Container, Row, Col } from "../components/Grid"
import BodyText from "../components/BodyText"

import Button from "../components/Button"
import Backlink from "../components/Backlink"
import Link from "../components/Link"
import Box from "../components/Box"
import ImageContentful from "../components/ImageContentful"
import Spacer from "../components/Spacer"
import Snippet from "../components/Snippet"
import Seo from "../components/Seo"

import StyledArticleTeaser from "../components/ArticleTeaser"
import SocialShareVertical from "../components/SocialShareVertical"

import { makeFastlink } from "../helpers"
import { media, mixins } from "../styles"
import { headerHeightLg } from "../components/Header"

export const pageQuery = graphql`
  query ContentfulSecurityArticle($slug: String, $language: String) {
    site {
      siteMetadata {
        siteUrl
      }
    }
    contentfulPageMetadata(
      node_locale: { eq: $language }
      pagePath: { eq: "/security/" }
    ) {
      ...PageMetadata
    }
    contentfulArticle(slug: { eq: $slug }, node_locale: { eq: $language }) {
      contentful_id
      title
      date(formatString: "LL", locale: $language)
      coverImage {
        ...ContentfulImageData
        gatsbyImageData(
          height: 550
          layout: CONSTRAINED
          quality: 75
          resizingBehavior: THUMB
          width: 925
          placeholder: BLURRED
        )
      }
      socialShareImageOpengraph: coverImage {
        ...ContentfulImageData
        gatsbyImageData(
          layout: FIXED
          width: 1200
          height: 628
          quality: 85
          formats: JPG
          resizingBehavior: THUMB
        )
      }
      socialShareImageTwitter: coverImage {
        ...ContentfulImageData
        gatsbyImageData(
          layout: FIXED
          width: 1024
          height: 512
          quality: 85
          formats: JPG
          resizingBehavior: THUMB
        )
      }
      # ctaHeader
      # ctaLinkText
      # ctaLinkUrl
      ctaType
      metaDescription
      keywords
      description
      text {
        childMarkdownRemark {
          excerpt(truncate: true, pruneLength: 150, format: PLAIN)
          html
          timeToRead
        }
      }
      slug
    }
    relatedArticles: allContentfulArticle(
      filter: {
        articlecollection: { elemMatch: { slug: { eq: "security" } } }
        node_locale: { eq: $language }
        slug: { ne: $slug }
      }
      limit: 3
      sort: { date: DESC }
    ) {
      nodes {
        articlecollection {
          slug
        }
        date(formatString: "LL", locale: "en")
        description
        text {
          childMarkdownRemark {
            excerpt(truncate: true, pruneLength: 150, format: PLAIN)
            html
            timeToRead
          }
        }
        slug
        title
      }
    }
  }
`

const Wrapper = styled.div`
  background: ${({ theme }) => theme.greyLight};
  padding: 30px 0;

  @media ${media.md} {
    padding: 65px 0 5px;
  }

  @media ${media.xl} {
    padding: 45px 0 60px;
  }
`
const Header = styled.div`
  margin: 0 0 20px;
  padding: 15px 0 0;

  @media ${media.sm} {
    background: none;
  }

  @media ${media.xl} {
    margin: 0 0 25px;
    padding: 35px 0 0;
  }
`

const PostTitle = styled.h1`
  ${mixins.title3}
  margin: 0 0 15px;

  @media ${media.xl} {
    margin: 0 0 25px;
  }
`

const PostBody = styled.div`
  background: ${({ theme }) => theme.white};
  padding: 30px 15px 40px;
  margin: 0 -15px;

  @media ${media.sm} {
    padding: 0;
    margin: 0;
  }

  @media print {
    padding-bottom: 0;
  }
`

const TextWidth = styled.div`
  max-width: 700px;
  margin: 0 auto;
  width: 100%;
`

const BackLinkContainer = styled.div`
  margin: 0 20px;

  @media ${media.lg} {
    margin: 0 auto 20px;
    max-width: 720px;
  }

  @media ${media.xl} {
    max-width: 1024px;
  }
`

const Content = styled.div`
  @media ${media.sm} {
    background: ${({ theme }) => theme.white};
    margin: 0 20px;
    padding: 20px 30px 60px;
  }

  @media ${media.lg} {
    margin: 0 auto;
    max-width: 720px;
    padding: 20px 35px 60px;
  }

  @media ${media.xl} {
    margin: 0 auto;
    padding: 20px 50px 100px;
    max-width: 1024px;
  }
`

const CoverImage = styled.div`
  margin: 0 -15px;

  @media ${media.sm} {
    margin: 0 0 30px;
  }

  @media ${media.lg} {
    margin: 0 0 35px;
  }

  @media ${media.xl} {
    margin: 0 0 55px;
  }
`

const ReadingTime = styled.p`
  color: ${({ theme }) => theme.sage};
  margin: 0 0 20px;

  @media ${media.xl} {
    margin: 0 0 30px;
  }
`

const Share = styled.div`
  margin: 0 0 60px;

  @media ${media.lg} {
    margin: 0 0 30px;
    position: sticky;
    top: ${headerHeightLg + 20}px;
  }
`

const CtaBox = styled(Box)`
  @media print {
    display: none;
  }
`

const Cta = styled.div`
  padding: 25px 30px 30px;
  text-align: center;

  @media ${media.md} {
    padding: 30px 40px 40px;
  }

  @media ${media.xl} {
    padding: 35px 60px 40px;
  }

  h3 {
    ${mixins.copy3}
    margin: 0 0 20px;

    @media ${media.md} {
      margin: 0 0 30px;
    }

    @media ${media.xl} {
      margin: 0 0 15px;
    }
  }

  a {
    text-decoration: none;
  }
`

const ArticleList = styled.div`
  display: flex;
  flex-direction: column;

  @media ${media.md} {
    padding: 0 15px;
    flex-flow: row wrap;
    justify-content: space-between;
  }

  @media ${media.xl} {
    padding: 0 80px;
  }

  .post-teaser {
    margin: 0 0 30px;

    @media ${media.md} {
      width: calc(50% - 15px);
    }

    @media ${media.xl} {
      margin: 0 0 80px;
      width: calc(50% - 50px);
    }
  }
`

const Related = styled.div`
  @media print {
    display: none;
  }
`

const RelatedHeader = styled.h3`
  ${mixins.title5}
  color: ${({ theme }) => theme.sage};
  text-align: center;
  margin: 0 0 25px;

  @media ${media.xl} {
    margin: 0 0 65px;
  }
`

const ArticleTeaser = styled(StyledArticleTeaser)`
  background: ${({ theme }) => theme.white};
`

const standardCta = "securityArticleStandardCTA"

const ContentfulSecurityArticleTemplate = (props) => {
  const intl = useIntl()
  const article = props.data.contentfulArticle
  const relatedArticles = props.data.relatedArticles.nodes
  const socialShareImages = {}
  const pageMetadata = props.data.contentfulPageMetadata

  if (
    article.socialShareImageOpengraph?.gatsbyImageData?.images?.fallback?.src
  ) {
    socialShareImages.socialShareImageOpengraph =
      article.socialShareImageOpengraph.gatsbyImageData.images.fallback.src
  }

  if (article.socialShareImageTwitter?.gatsbyImageData?.images?.fallback?.src) {
    socialShareImages.socialShareImageTwitter =
      article.socialShareImageTwitter.gatsbyImageData.images.fallback.src
  }

  return (
    <>
      <Seo
        {...pageMetadata}
        title={`${article.title} | ${pageMetadata.title}`}
        description={
          article.metaDescription ||
          article.description ||
          article.text?.childMarkdownRemark?.excerpt
        }
        keywords={article.keywords?.join(", ")}
        {...socialShareImages}
      />
      <Layout pageId={article.contentful_id}>
        <Wrapper>
          <Container>
            <Row>
              <Col lg="10" lgOffset="1">
                <BackLinkContainer>
                  <Backlink to={`/security/`} />
                </BackLinkContainer>
                <Content>
                  <Header>
                    <PostTitle>{article.title}</PostTitle>
                    <ReadingTime>
                      {article.text?.childMarkdownRemark?.timeToRead}{" "}
                      {intl.formatMessage({ id: "articles.readingTime" })}
                    </ReadingTime>
                  </Header>
                  {article.coverImage && (
                    <CoverImage>
                      <ImageContentful
                        alt={article.title}
                        image={article.coverImage}
                      />
                    </CoverImage>
                  )}
                  <PostBody>
                    <Row>
                      <Col lg="9" lgOrder="2" xl="10">
                        <TextWidth>
                          <BodyText>
                            <div
                              dangerouslySetInnerHTML={{
                                __html: article.text?.childMarkdownRemark?.html,
                              }}
                            />
                          </BodyText>
                        </TextWidth>
                      </Col>
                      <Col lg="2" lgOrder="1" xl="1">
                        <Share>
                          <SocialShareVertical
                            text={article.title}
                            url={`${props.data.site.siteMetadata.siteUrl}/${intl.locale}/security/${article.slug}/`}
                          />
                        </Share>
                      </Col>
                    </Row>
                    <TextWidth>
                      <CtaBox>
                        <Cta>
                          <h3>
                            {article.ctaType === "Custom" && article.ctaHeader
                              ? article.ctaHeader
                              : Snippet({ id: standardCta, field: "header" })}
                          </h3>
                          <Link
                            to={makeFastlink(
                              article.ctaType === "Custom" && article.ctaLinkUrl
                                ? article.ctaLinkUrl
                                : Snippet({ id: standardCta, field: "linkUrl" })
                            )}
                          >
                            <Button as="span" outline arrow>
                              {article.ctaType === "Custom" &&
                              article.ctaLinkText
                                ? article.ctaLinkText
                                : Snippet({
                                    id: standardCta,
                                    field: "linkText",
                                  })}
                            </Button>
                          </Link>
                        </Cta>
                      </CtaBox>
                    </TextWidth>
                  </PostBody>
                </Content>
              </Col>
            </Row>
            <Spacer height={50} xl={85} print={0} />
            <Related>
              <RelatedHeader>
                {intl.formatMessage({ id: "articles.furtherReading" })}
              </RelatedHeader>
              <ArticleList>
                {relatedArticles.map((relatedArticle) => (
                  <ArticleTeaser
                    key={relatedArticle.slug}
                    article={relatedArticle}
                    to={`/security/${relatedArticle.slug}/`}
                    header={relatedArticle.title}
                    text={
                      relatedArticle.description ||
                      relatedArticle.text?.childMarkdownRemark?.excerpt
                    }
                  />
                ))}
              </ArticleList>
            </Related>
          </Container>
        </Wrapper>
      </Layout>
    </>
  )
}

export default ContentfulSecurityArticleTemplate
